import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, forkJoin, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
} from 'rxjs/operators';

import { ProfileSectionEditing, RequestStatus } from '@patient-ui/shared/enums';
import {
  CardType,
  IAddress,
  IAppointment,
  INotificationPreference,
  IPatient,
  IPatientDemographicsUpdate,
  IPhone,
  NotificationPreference,
  Patient,
  PaymentMethod,
  PaymentType,
} from '@patient-ui/shared/models';
import { patientFactory } from '@patient-ui/shared/utils';

import { PatientState } from './patient.reducer';
import { patientQuery } from './patient.selectors';
import { PatientService } from './patient.service';
import * as LinkedAccountsActions from '../linked-accounts/linked-accounts.actions';
import { linkedAccountsQuery } from '../linked-accounts/linked-accounts.selectors';
import * as patientActions from '../patient/patient.actions';
import * as ProfileActions from '../profile/profile.actions';

@Injectable()
export class PatientEffects {
  loadLoggedInPatient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.loadLoggedInPatient),
      exhaustMap((action) =>
        this.patientService.populatePatient(action.payload).pipe(
          map((payload: Patient) =>
            patientActions.loadLoggedInPatientSuccess({ payload })
          ),
          catchError((_error) =>
            of(patientActions.loadLoggedInPatientFailure())
          )
        )
      )
    )
  );

  callToLoadLinkedAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.loadLoggedInPatientSuccess),
      map(() => LinkedAccountsActions.loadLinkedAccounts()),
      catchError(() => EMPTY)
    )
  );

  loadPatientAppointments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.loadPatientAppointments),
      concatLatestFrom(() =>
        this.patientStore.select(
          linkedAccountsQuery.selectGetLinkedAccountsStatus
        )
      ),
      switchMap(([_action, linkedAccountsStatus]) => {
        // If linked accounts haven't started loading yet, trigger load first
        if (linkedAccountsStatus === RequestStatus.NotSent) {
          this.patientStore.dispatch(
            LinkedAccountsActions.loadLinkedAccounts()
          );
        }

        // Wait for linked accounts to finish loading (either success or failure)
        return this.patientStore
          .select(linkedAccountsQuery.selectGetLinkedAccountsStatus)
          .pipe(
            filter(
              (status) =>
                status === RequestStatus.Success ||
                status === RequestStatus.Failure
            ),
            take(1), // Take only the first completion
            switchMap(() =>
              this.patientStore.select(
                linkedAccountsQuery.selectRawLinkedAccountsList
              )
            ),
            take(1),
            exhaustMap((linkedAccounts) => {
              // Pass includeLinkedAccounts flag if user has linked accounts
              const includeLinkedAccounts =
                linkedAccounts && linkedAccounts.length > 0;
              return this.patientService
                .getAppointments(includeLinkedAccounts)
                .pipe(
                  switchMap((appointments: IAppointment[]) => [
                    patientActions.loadPatientAppointmentsSuccess({
                      appointments,
                    }),
                    // Trigger background insurance eligibility checks
                    patientActions.checkInsuranceEligibility({ appointments }),
                  ]),
                  catchError((error) =>
                    of(patientActions.loadPatientAppointmentsFailure({ error }))
                  )
                );
            })
          );
      })
    )
  );

  getEncryptedLpid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.getEncryptedLpid),
      switchMap((action) =>
        this.patientService.getEncryptedLPID(action.patientId).pipe(
          map((encryptedLpid) =>
            patientActions.getEncryptedLpidSuccess({
              encryptedLpid,
            })
          ),
          catchError((error) =>
            of(patientActions.getEncryptedLpidFailure({ error }))
          )
        )
      )
    )
  );

  createOrUpdateOrDeleteAddressSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.createOrUpdateOrDeleteAddressSuccess),
      concatLatestFrom((_status) =>
        this.patientStore.select(
          patientQuery.selectCreateOrUpdateAddressRequestStatus
        )
      ),
      exhaustMap(([_action, status]) =>
        this.patientService
          .updatePatientAddress(
            status.addressOwner as IPatient,
            status.addressId ? status.addressId : _action.response,
            status.addressAction,
            status.address as IAddress,
            status.switchToPrimary
          )
          .pipe(
            switchMap((updatedPatient: Patient) => [
              patientActions.updatePatientAfterProfileChange({
                updatedPatient: updatedPatient,
              }),
              ProfileActions.setSectionInEditMode({
                section: ProfileSectionEditing.none,
              }),
            ]),
            catchError((_error) =>
              of(patientActions.upadatePatientProfileFailure())
            )
          )
      )
    )
  );

  createOrUpdateAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.createOrUpdateAddress),
      exhaustMap((action) =>
        this.patientService
          .createOrUpdateAddress(action.addressToUpdate, action.dependentId)
          .pipe(
            map((response) =>
              patientActions.createOrUpdateOrDeleteAddressSuccess({
                response: response,
              })
            ),
            catchError((error) =>
              of(
                patientActions.createOrUpdateOrDeleteAddressFailure({
                  errorResponse: error,
                })
              )
            )
          )
      )
    )
  );

  deleteAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.deleteAddress),
      exhaustMap((action) =>
        this.patientService.deleteAddress(action.addressToDelete).pipe(
          map((response) =>
            patientActions.createOrUpdateOrDeleteAddressSuccess({
              response: response,
            })
          ),
          catchError((error) =>
            of(
              patientActions.createOrUpdateOrDeleteAddressFailure({
                errorResponse: error,
              })
            )
          )
        )
      )
    )
  );

  createOrUpdatePhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.createOrUpdatePhone),
      exhaustMap((action) =>
        this.patientService
          .createOrUpdatePhone(action.phoneToUpdate, action.dependentId)
          .pipe(
            map((response) =>
              patientActions.createOrUpdateOrDeletePhoneSuccess({
                response: response,
              })
            ),
            catchError((error) =>
              of(
                patientActions.createOrUpdateOrDeletePhoneFailure({
                  errorResponse: error,
                })
              )
            )
          )
      )
    )
  );

  deletePhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.deletePhone),
      exhaustMap((action) =>
        this.patientService.deletePhone(action.phoneToDelete).pipe(
          map((response) =>
            patientActions.createOrUpdateOrDeletePhoneSuccess({
              response: response,
            })
          ),
          catchError((error) =>
            of(
              patientActions.createOrUpdateOrDeletePhoneFailure({
                errorResponse: error,
              })
            )
          )
        )
      )
    )
  );

  createOrUpdateOrDeletePhoneSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.createOrUpdateOrDeletePhoneSuccess),
      concatLatestFrom((_status) =>
        this.patientStore.select(
          patientQuery.selectCreateOrUpdatePhoneRequestStatus
        )
      ),
      exhaustMap(([_action, status]) =>
        this.patientService
          .updatePatientPhone(
            status.phoneOwner as IPatient,
            status.phoneId as number,
            status.phoneAction,
            status.phone as IPhone,
            status.switchToPrimary
          )
          .pipe(
            switchMap((updatedPatient: Patient) => [
              patientActions.updatePatientAfterProfileChange({
                updatedPatient: updatedPatient,
              }),
              ProfileActions.setSectionInEditMode({
                section: ProfileSectionEditing.none,
              }),
            ]),
            catchError((_error) =>
              of(patientActions.upadatePatientProfileFailure())
            )
          )
      )
    )
  );

  updatePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.updatePassword),
      exhaustMap((action) =>
        this.patientService.updatePassword(action.passwords).pipe(
          map((_response) => patientActions.updatePasswordSuccess()),
          catchError((error) =>
            of(patientActions.updatePasswordFailure({ errorResponse: error }))
          )
        )
      )
    )
  );

  createDependent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.createDependent),
      exhaustMap((action) =>
        this.patientService.createDependent(action.dependentRegistration).pipe(
          map((response) => {
            if (response.status === 201 && !!response.body) {
              const dependent = patientFactory(response.body, true);
              dependent.relationshipType =
                action.dependentRegistration.relationshipType;
              dependent.usePrimaryAddressOfPrimaryPatient =
                action.dependentRegistration.usePrimaryAddressOfPrimaryPatient;
              dependent.usePrimaryEmailOfPrimaryPatient =
                action.dependentRegistration.usePrimaryEmailOfPrimaryPatient;
              dependent.usePrimaryPhoneOfPrimaryPatient =
                action.dependentRegistration.usePrimaryPhoneOfPrimaryPatient;
              return patientActions.createDependentSucess({
                dependent,
              });
            }
            return patientActions.createDependentFailure({
              errorResponse: response,
              dependentRegistration: action.dependentRegistration,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              patientActions.createDependentFailure({
                errorResponse: error,
                dependentRegistration: action.dependentRegistration,
              })
            )
          )
        )
      )
    )
  );

  deleteDependent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.deleteDependent),
      exhaustMap((action) =>
        this.patientService.deleteDependent(action.depId).pipe(
          map(() =>
            patientActions.deleteDependentSuccess({ depId: action.depId })
          ),
          catchError((_error) => of(patientActions.deleteDependentFailure()))
        )
      )
    )
  );

  updatePatientDemographics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.updatePatientDemographics),
      exhaustMap((action) =>
        this.patientService
          .updatePatientDemographics(
            action.patientDemographics,
            action.dependentId
          )
          .pipe(
            switchMap((response) => [
              patientActions.updatePatientDemographicsSuccess({
                response: response as NotificationPreference,
              }),
              ProfileActions.setSectionInEditMode({
                section: ProfileSectionEditing.none,
              }),
            ]),
            catchError((error) =>
              of(patientActions.updatePasswordFailure({ errorResponse: error }))
            )
          )
      )
    )
  );

  updatePatientDemographicsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.updatePatientDemographicsSuccess),
      concatLatestFrom((_status) =>
        this.patientStore.select(
          patientQuery.selectPatientDetailsForDemographicUpdate
        )
      ),
      exhaustMap(([_action, status]) =>
        this.patientService
          .updatePatientWithNewDemographics(
            status.patientToUpdate as IPatient,
            status.demographicsToUpdate as IPatientDemographicsUpdate
          )
          .pipe(
            switchMap((updatedPatient: Patient) => [
              patientActions.updatePatientAfterProfileChange({
                updatedPatient: updatedPatient,
              }),
              ProfileActions.setSectionInEditMode({
                section: ProfileSectionEditing.none,
              }),
            ]),
            catchError((_error) =>
              of(patientActions.upadatePatientProfileFailure())
            )
          )
      )
    )
  );

  updatePatientNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.updatePatientNotifications),
      exhaustMap((action) =>
        this.patientService
          .updatePatientNotifications(
            action.patientNotifications,
            action.dependentId
          )
          .pipe(
            switchMap((response) => [
              patientActions.updatePatientNotificationsSuccess({
                response: response,
              }),
              ProfileActions.setSectionInEditMode({
                section: ProfileSectionEditing.none,
              }),
            ]),
            catchError((error) =>
              of(
                patientActions.updatePatientNotificationsFailure({
                  errorResponse: error,
                })
              )
            )
          )
      )
    )
  );

  updatePatientNotificationsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.updatePatientNotificationsSuccess),
      concatLatestFrom((_status) =>
        this.patientStore.select(
          patientQuery.selectPatientDetailsForNotificationsUpdate
        )
      ),
      exhaustMap(([_action, status]) =>
        this.patientService
          .updatePatientWithNewNotifications(
            status.patientToUpdate as IPatient,
            status.notificationsToUpdate as INotificationPreference[],
            status.notificationPrimaryEmail as string
          )
          .pipe(
            switchMap((updatedPatient: Patient) => [
              patientActions.updatePatientAfterProfileChange({
                updatedPatient: updatedPatient,
              }),
              ProfileActions.setSectionInEditMode({
                section: ProfileSectionEditing.none,
              }),
            ]),
            catchError((_error) =>
              of(patientActions.upadatePatientProfileFailure())
            )
          )
      )
    )
  );

  /**
   * Refresh linked accounts after profile changes (phone/address/email)
   * When primary account holder updates contact info, backend may update linked accounts' notification emails
   */
  refreshLinkedAccountsAfterProfileChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.updatePatientAfterProfileChange),
      map(() => LinkedAccountsActions.loadLinkedAccounts())
    )
  );

  getPatientWallet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.getPatientWallet),
      exhaustMap(() =>
        this.patientService.getPatientWallet().pipe(
          map((response) => {
            const paymentMethodArray: PaymentMethod[] = [];
            if (response.isSuccessful) {
              if (response.listWalletPayments.length > 0) {
                response.listWalletPayments.forEach((walletInfo) => {
                  const paymentMethod: PaymentMethod = new PaymentMethod();
                  paymentMethod.walletId = walletInfo.walletId
                    ? walletInfo.walletId
                    : '';
                  paymentMethod.walletName = walletInfo.walletName
                    ? walletInfo.walletName
                    : '';
                  paymentMethod.cardId = walletInfo.cardId
                    ? walletInfo.cardId
                    : '';
                  paymentMethod.firstName = walletInfo.firstName
                    ? walletInfo.firstName
                    : '';
                  paymentMethod.lastName = walletInfo.lastName
                    ? walletInfo.lastName
                    : '';
                  paymentMethod.addressLine1 = walletInfo.addressline1
                    ? walletInfo.addressline1
                    : '';
                  paymentMethod.addressLine2 = walletInfo.addressline2
                    ? walletInfo.addressline2
                    : '';
                  paymentMethod.city = walletInfo.city ? walletInfo.city : '';
                  paymentMethod.state = walletInfo.state
                    ? walletInfo.state
                    : '';
                  paymentMethod.zip = walletInfo.zip ? walletInfo.zip : '';
                  paymentMethod.country = walletInfo.country
                    ? walletInfo.country
                    : '';
                  paymentMethod.cardType = walletInfo.cardType as CardType;
                  paymentMethod.institutionName = this.getCardName(
                    walletInfo.cardType as CardType
                  );
                  paymentMethod.debitLast4 = walletInfo.maskCard;
                  paymentMethod.accountType = walletInfo.accountType
                    ? walletInfo.accountType
                    : '';
                  paymentMethod.routingNumber = walletInfo.routingNumber
                    ? walletInfo.routingNumber
                    : '';
                  paymentMethod.expirationDate =
                    walletInfo.expMon + '/' + walletInfo.expYr;
                  paymentMethod.accountId = walletInfo.accountId
                    ? walletInfo.accountId
                    : '';
                  paymentMethod.token = walletInfo.token
                    ? walletInfo.token
                    : '';
                  paymentMethod.paymentType =
                    walletInfo.paymentMethod === 'CC'
                      ? PaymentType.CREDIT
                      : PaymentType.ECHECK;
                  paymentMethod.invalid = walletInfo.invalid
                    ? walletInfo.invalid
                    : false;
                  paymentMethodArray.push(paymentMethod);
                });
              }
              return patientActions.getPatientWalletSuccess({
                payload: paymentMethodArray,
              });
            } else {
              return patientActions.getPatientWalletFailure();
            }
          }),
          catchError(() => of(patientActions.getPatientWalletFailure()))
        )
      )
    )
  );

  addPaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.addPaymentMethod),
      exhaustMap((action) =>
        this.patientService.addPaymentMethod(action.payload).pipe(
          switchMap((response) => [
            patientActions.getPatientWallet(),
            patientActions.addPaymentMethodSuccess({ payload: response }),
          ]),
          catchError(() => of(patientActions.addPaymentMethodFailure()))
        )
      )
    )
  );

  deletePaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.deletePaymentMethod),
      exhaustMap((action) =>
        this.patientService
          .deletePaymentMethod(
            action.payload.cardId,
            action.payload.forceDelete
          )
          .pipe(
            map((response) =>
              patientActions.deletePaymentMethodSuccess({
                payload: response,
              })
            ),
            catchError((response) =>
              of(
                patientActions.deletePaymentMethodFailure({ payload: response })
              )
            )
          )
      )
    )
  );

  setClinicalTrialsPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.setClinicalTrialsPreference),
      exhaustMap((action) =>
        this.patientService.setClinicalTrialPreference(action.payload).pipe(
          map(() =>
            patientActions.setClinicalTrialsPreferenceSuccess({
              payload: action.payload,
            })
          ),
          catchError((_error) =>
            of(patientActions.setClinicalTrialsPreferenceFailure())
          )
        )
      )
    )
  );

  updateAuthenticationQuizStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.updateAuthenticationQuizStatus),
      exhaustMap((action) =>
        this.patientService
          .populateUserListWithDependents(
            action.updatedPatient,
            action.primaryAccountHolder,
            action.dependents
          )
          .pipe(
            switchMap((_response) => [
              patientActions.updateAuthQuizStatus({
                updatedPatient: action.updatedPatient,
              }),
              patientActions.updatePatientUserList({ payload: _response }),
              LinkedAccountsActions.updateLinkedPortalUserListWithPAH(),
            ]),
            catchError(() =>
              of(
                patientActions.updateAuthQuizStatus({
                  updatedPatient: action.updatedPatient,
                })
              )
            )
          )
      )
    )
  );

  retrieveActivePayments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.retrieveActivePayments),
      exhaustMap(() =>
        this.patientService.retrieveActivePayments().pipe(
          switchMap((_response) => [
            patientActions.retrieveActivePaymentsSuccess({
              activePayments: _response,
            }),
          ]),
          catchError((error) =>
            of(patientActions.retrieveActivePaymentsFailure({ error }))
          )
        )
      )
    )
  );

  /**
   * Check insurance eligibility for appointments with evOverride: false
   * First fetches encrypted lpid for appointments that don't have it
   * Then processes up to 3 appointments concurrently to avoid overwhelming the backend
   */
  checkInsuranceEligibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(patientActions.checkInsuranceEligibility),
      switchMap(({ appointments }) => {
        // Filter appointments that need eligibility check (upcoming only, evOverride = false)
        const appointmentsNeedingCheck = appointments.filter(
          (apt) =>
            !apt.past &&
            apt.insuranceInfo &&
            apt.insuranceInfo.some((ins) => ins.evOverride === false)
        );

        if (appointmentsNeedingCheck.length === 0) {
          return EMPTY;
        }

        // Get unique patient IDs that need lpid
        const patientIdsNeedingLpid = Array.from(
          new Set(
            appointmentsNeedingCheck
              .filter((apt) => !apt.lpid)
              .map((apt) => apt.patientId)
          )
        );

        // If no lpids needed, proceed with checks
        if (patientIdsNeedingLpid.length === 0) {
          return this.processInsuranceChecks(appointmentsNeedingCheck);
        }

        // Fetch lpids first for all unique patient IDs
        return forkJoin(
          patientIdsNeedingLpid.map((patientId) =>
            this.patientService.getEncryptedLPID(parseInt(patientId, 10)).pipe(
              map((encryptedLpid) => ({ patientId, encryptedLpid })),
              catchError(() => of({ patientId, encryptedLpid: null }))
            )
          )
        ).pipe(
          switchMap((lpidResults) => {
            // Map patient IDs to lpids
            const lpidMap = new Map(
              lpidResults
                .filter((r) => r.encryptedLpid)
                .map((r) => [r.patientId, r.encryptedLpid])
            );

            // Update appointments with lpids
            const appointmentsWithLpid = appointmentsNeedingCheck.map(
              (apt) => ({
                ...apt,
                lpid: apt.lpid || lpidMap.get(apt.patientId) || undefined,
              })
            );

            // Filter to only appointments with valid lpid
            const appointmentsToCheck = appointmentsWithLpid.filter(
              (apt) => apt.lpid
            ) as IAppointment[];

            return this.processInsuranceChecks(appointmentsToCheck);
          })
        );
      })
    )
  );

  /**
   * Helper method to process insurance eligibility checks
   * Processes appointments with concurrency limit of 3
   */
  private processInsuranceChecks(appointments: IAppointment[]) {
    if (appointments.length === 0) {
      return EMPTY;
    }

    return of(...appointments).pipe(
      mergeMap(
        (appointment) => {
          if (!appointment.lpid) {
            return EMPTY;
          }

          return this.patientService
            .searchInsuranceEligibility(
              appointment.confirmationNum,
              appointment.lpid
            )
            .pipe(
              map((insurances) => {
                // Check if ANY insurance in search result is eligible
                const hasEligibleInsurance = insurances.some(
                  (ins) => ins.eligible === true
                );

                return patientActions.updateAppointmentInsuranceEligibility({
                  confirmationNum: appointment.confirmationNum,
                  insuranceSearchEligible: hasEligibleInsurance,
                });
              }),
              // On error, don't block other checks - just skip this appointment
              catchError(() => EMPTY)
            );
        },
        3 // Concurrency limit: process max 3 at a time
      )
    );
  }

  constructor(
    private actions$: Actions,
    private patientService: PatientService,
    private patientStore: Store<PatientState>
  ) {}

  getCardName(_cardTypeCD: CardType) {
    switch (_cardTypeCD) {
      case 'AX':
        return 'AmericanExpress';
      case 'DI':
        return 'Discover';
      case 'MC':
        return 'Mastercard';
      case 'VI':
        return 'Visa';
      case 'EC':
        return 'Bank Payment';
      default:
        return 'OTHER';
    }
  }
}
